/**
 * ContractFormModal - Модальное окно для создания/редактирования договора
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  TextInput,
  Textarea,
  Select,
  MultiSelect,
  Button,
  Group,
  Stack,
  Text,
  Alert,
  Box,
  Badge,
  rem,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { DateInput } from '@mantine/dates';
import {
  IconAlertCircle,
  IconUpload,
  IconFile,
  IconCalendar,
  IconHash,
  IconCloudUpload,
  IconX,
  IconDownload,
} from '@tabler/icons-react';
import tokens from '../../../theme/designTokens';

// Статусы договоров
const CONTRACT_STATUSES = [
  { value: 'draft', label: 'Черновик' },
  { value: 'pending_signature', label: 'На подписании' },
  { value: 'signed', label: 'Подписан' },
  { value: 'active', label: 'Действует' },
  { value: 'paused', label: 'Приостановлен' },
  { value: 'terminated', label: 'Расторгнут' },
];

// Разрешённые типы файлов
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'text/plain',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
];

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

const ContractFormModal = ({
  opened,
  onClose,
  onSubmit,
  contract = null, // null = создание, object = редактирование
  requisites, // Реквизиты, к которым привязывается договор
  organizationId,
  subjects = [], // Список предметов для выбора
  loading = false,
}) => {
  const isEditing = !!contract;
  const openRef = useRef(null);

  const [formData, setFormData] = useState({
    contract_number: '',
    signing_date: null,
    start_date: null,
    subject_ids: [],
    comment: '',
    status: 'draft',
  });

  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  // Инициализация формы при открытии
  useEffect(() => {
    if (opened) {
      if (contract) {
        setFormData({
          contract_number: contract.contract_number || '',
          signing_date: contract.signing_date ? new Date(contract.signing_date) : null,
          start_date: contract.start_date ? new Date(contract.start_date) : null,
          subject_ids: contract.subjects?.map(s => s.id.toString()) || [],
          comment: contract.comment || '',
          status: contract.status || 'draft',
        });
        setFile(null); // Файл не подгружаем при редактировании
      } else {
        setFormData({
          contract_number: '',
          signing_date: null,
          start_date: null,
          subject_ids: [],
          comment: '',
          status: 'draft',
        });
        setFile(null);
      }
      setErrors({});
      setSubmitError(null);
    }
  }, [opened, contract]);

  // Валидация поля
  const validateField = (name, value) => {
    let error = null;

    switch (name) {
      case 'signing_date':
        if (!value && !isEditing) error = 'Дата подписания обязательна';
        break;
      case 'start_date':
        if (!value && !isEditing) error = 'Дата начала занятий обязательна';
        break;
      case 'subject_ids':
        if ((!value || value.length === 0) && !isEditing) error = 'Выберите хотя бы один предмет';
        break;
      case 'file':
        if (!value && !isEditing) error = 'Загрузите файл договора';
        break;
      default:
        break;
    }

    setErrors(prev => ({ ...prev, [name]: error }));
    return error;
  };

  // Обработка изменения поля
  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name !== 'contract_number' && name !== 'comment') {
      validateField(name, value);
    }
  };

  // Обработка изменения файла
  const handleFileChange = (selectedFile) => {
    if (selectedFile) {
      // Проверка типа
      if (!ALLOWED_MIME_TYPES.includes(selectedFile.type)) {
        setErrors(prev => ({
          ...prev,
          file: 'Недопустимый тип файла. Разрешены: PDF, TXT, DOC, DOCX, JPG, PNG',
        }));
        setFile(null);
        return;
      }

      // Проверка размера
      if (selectedFile.size > MAX_FILE_SIZE) {
        setErrors(prev => ({
          ...prev,
          file: 'Файл слишком большой. Максимум 25 МБ',
        }));
        setFile(null);
        return;
      }

      setFile(selectedFile);
      setErrors(prev => ({ ...prev, file: null }));
    } else {
      setFile(null);
      if (!isEditing) {
        setErrors(prev => ({ ...prev, file: 'Загрузите файл договора' }));
      }
    }
  };

  // Полная валидация формы
  const validateForm = () => {
    const newErrors = {};

    if (!isEditing) {
      // При создании все поля обязательны
      if (!file) {
        newErrors.file = 'Загрузите файл договора';
      }
      if (!formData.signing_date) {
        newErrors.signing_date = 'Дата подписания обязательна';
      }
      if (!formData.start_date) {
        newErrors.start_date = 'Дата начала занятий обязательна';
      }
      if (!formData.subject_ids || formData.subject_ids.length === 0) {
        newErrors.subject_ids = 'Выберите хотя бы один предмет';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Отправка формы
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    try {
      const data = {
        contract_number: formData.contract_number?.trim() || null,
        signing_date: formData.signing_date
          ? formData.signing_date.toISOString().split('T')[0]
          : null,
        start_date: formData.start_date
          ? formData.start_date.toISOString().split('T')[0]
          : null,
        subject_ids: formData.subject_ids.map(id => parseInt(id)),
        comment: formData.comment?.trim() || null,
        status: formData.status,
      };

      await onSubmit(data, file);
    } catch (err) {
      setSubmitError(err.message || 'Ошибка при сохранении договора');
    }
  };

  // Опции для MultiSelect предметов
  const subjectOptions = subjects.map(s => ({
    value: s.id.toString(),
    label: s.name,
  }));

  // Форматирование размера файла
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEditing ? 'Редактировать договор' : 'Добавить договор'}
      size="lg"
      radius="xl"
      styles={{
        content: {
          borderRadius: '16px',
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {submitError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {submitError}
            </Alert>
          )}

          {/* Информация о реквизитах */}
          {requisites && (
            <Box
              p="sm"
              style={{
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
              }}
            >
              <Text size="xs" c="dimmed" mb={4}>
                Договор для реквизитов
              </Text>
              <Text size="sm" fw={500}>
                {requisites.company_name}
              </Text>
              <Text size="xs" c="dimmed">
                ИНН: {requisites.inn}
              </Text>
            </Box>
          )}

          {/* Файл договора - Dropzone */}
          <Box>
            <Text size="sm" fw={500} mb={4}>
              Файл договора {!isEditing && <span style={{ color: 'red' }}>*</span>}
            </Text>

            {/* Показываем выбранный файл */}
            {file ? (
              <Box
                p="md"
                style={{
                  border: `2px solid ${tokens.colors.semantic.success.primary}`,
                  borderRadius: rem(8),
                  backgroundColor: tokens.colors.semantic.success.lighter || '#e8f5e9',
                }}
              >
                <Group justify="space-between">
                  <Group gap="sm">
                    <IconFile size={24} color={tokens.colors.semantic.success.primary} />
                    <Box>
                      <Text size="sm" fw={500}>{file.name}</Text>
                      <Text size="xs" c="dimmed">{formatFileSize(file.size)}</Text>
                    </Box>
                  </Group>
                  <Button
                    variant="subtle"
                    color="red"
                    size="xs"
                    onClick={() => setFile(null)}
                    leftSection={<IconX size={14} />}
                  >
                    Удалить
                  </Button>
                </Group>
              </Box>
            ) : (
              <>
                <Dropzone
                  openRef={openRef}
                  onDrop={(files) => handleFileChange(files[0])}
                  onReject={(files) => {
                    const rejection = files[0];
                    if (rejection?.errors?.[0]?.code === 'file-too-large') {
                      setErrors(prev => ({ ...prev, file: 'Файл слишком большой. Максимум 25 МБ' }));
                    } else if (rejection?.errors?.[0]?.code === 'file-invalid-type') {
                      setErrors(prev => ({ ...prev, file: 'Недопустимый тип файла' }));
                    } else {
                      setErrors(prev => ({ ...prev, file: 'Ошибка при загрузке файла' }));
                    }
                  }}
                  maxSize={MAX_FILE_SIZE}
                  accept={ALLOWED_MIME_TYPES}
                  multiple={false}
                  radius="md"
                  style={{
                    border: errors.file ? `2px dashed ${tokens.colors.semantic.error.primary}` : undefined,
                  }}
                >
                  <div style={{ pointerEvents: 'none' }}>
                    <Group justify="center">
                      <Dropzone.Accept>
                        <IconDownload
                          style={{ width: rem(52), height: rem(52) }}
                          color={tokens.colors.brand.primary}
                          stroke={1.5}
                        />
                      </Dropzone.Accept>
                      <Dropzone.Reject>
                        <IconX
                          style={{ width: rem(52), height: rem(52) }}
                          color={tokens.colors.semantic.error.primary}
                          stroke={1.5}
                        />
                      </Dropzone.Reject>
                      <Dropzone.Idle>
                        <IconCloudUpload
                          style={{ width: rem(52), height: rem(52) }}
                          color={tokens.colors.neutral[6]}
                          stroke={1.5}
                        />
                      </Dropzone.Idle>
                    </Group>

                    <Text ta="center" fw={500} size="lg" mt="md">
                      <Dropzone.Accept>Отпустите файл здесь</Dropzone.Accept>
                      <Dropzone.Reject>Файл не подходит</Dropzone.Reject>
                      <Dropzone.Idle>Загрузить файл договора</Dropzone.Idle>
                    </Text>
                    <Text ta="center" size="sm" c="dimmed" mt={7}>
                      Перетащите файл сюда или нажмите для выбора
                    </Text>
                    <Text ta="center" size="xs" c="dimmed" mt={4}>
                      PDF, DOC, DOCX, TXT, JPG, PNG до 25 МБ
                    </Text>
                  </div>
                </Dropzone>

                <Button
                  fullWidth
                  mt="md"
                  variant="default"
                  onClick={() => openRef.current?.()}
                  leftSection={<IconUpload size={16} />}
                >
                  Выбрать файл
                </Button>
              </>
            )}

            {errors.file && (
              <Text size="xs" c="red" mt={4}>
                {errors.file}
              </Text>
            )}

            {/* Текущий файл при редактировании */}
            {isEditing && contract?.current_file && !file && (
              <Group gap="xs" mt="sm">
                <IconFile size={14} color={tokens.colors.neutral[6]} />
                <Text size="xs" c="dimmed">
                  Текущий файл: {contract.current_file.original_filename} ({contract.current_file.file_size_display})
                </Text>
                <Badge size="xs" variant="light" color="blue" radius="xl">
                  v{contract.current_file.version_number}
                </Badge>
              </Group>
            )}

            {isEditing && !file && (
              <Text size="xs" c="dimmed" mt={4}>
                Загрузите новый файл для создания новой версии
              </Text>
            )}
          </Box>

          {/* Номер договора */}
          <TextInput
            label="Номер договора"
            placeholder="Например: 123/2025"
            leftSection={<IconHash size={16} />}
            value={formData.contract_number}
            onChange={(e) => handleChange('contract_number', e.target.value)}
            description="Необязательное поле"
          />

          {/* Даты */}
          <Group grow align="flex-start">
            <DateInput
              label="Дата подписания"
              placeholder="Выберите дату"
              required={!isEditing}
              leftSection={<IconCalendar size={16} />}
              value={formData.signing_date}
              onChange={(value) => handleChange('signing_date', value)}
              error={errors.signing_date}
              locale="ru"
              valueFormat="DD.MM.YYYY"
            />
            <DateInput
              label="Дата начала занятий"
              placeholder="Выберите дату"
              required={!isEditing}
              leftSection={<IconCalendar size={16} />}
              value={formData.start_date}
              onChange={(value) => handleChange('start_date', value)}
              error={errors.start_date}
              locale="ru"
              valueFormat="DD.MM.YYYY"
            />
          </Group>

          {/* Предметы */}
          <MultiSelect
            label="Предметы занятий"
            placeholder="Выберите предметы"
            required={!isEditing}
            data={subjectOptions}
            value={formData.subject_ids}
            onChange={(value) => handleChange('subject_ids', value)}
            error={errors.subject_ids}
            searchable
            clearable
            nothingFoundMessage="Предметы не найдены"
          />

          {/* Статус */}
          <Select
            label="Статус договора"
            data={CONTRACT_STATUSES}
            value={formData.status}
            onChange={(value) => handleChange('status', value)}
          />

          {/* Комментарий */}
          <Textarea
            label="Комментарий"
            placeholder="Дополнительная информация о договоре"
            value={formData.comment}
            onChange={(e) => handleChange('comment', e.target.value)}
            minRows={3}
            autosize
          />

          {/* Кнопки */}
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" loading={loading}>
              {isEditing ? 'Сохранить' : 'Добавить'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default ContractFormModal;
